import { Fragment, useMemo } from 'react';

import { Tooltip, makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { Flex, Text } from '@backstage/ui';

import { CountryUtils } from '@spotify/confidence-core-react';

import { useSegment } from '../../hooks';
import { DeltaText } from '../DeltaText';
import { useEvaluationContextField } from '../EvaluationContextFieldsContext';
import { Expression } from '../Expression';
import { ExpressionGroup } from '../ExpressionGroup';
import { FieldName } from '../FieldName/FieldName';
import { NormalizedExpression } from '../NormalizedExpression/types';
import { DeltaOperator, DeltaValue, NormalizedExpressionDelta } from './types';

const useStyles = makeStyles({
  addedBackground: {
    backgroundColor: 'var(--bui-bg-success)',
    '--bui-fg-primary': 'var(--bui-fg-success)',
    '--bui-fg-secondary':
      'color-mix(in srgb, var(--bui-fg-success) 80%, transparent)',
  },
  removedBackground: {
    backgroundColor: 'var(--bui-bg-danger)',
    '--bui-fg-primary': 'var(--bui-fg-danger)',
    '--bui-fg-secondary':
      'color-mix(in srgb, var(--bui-fg-danger) 80%, transparent)',
    color: 'var(--bui-fg-danger)',
    textDecoration: 'line-through',
  },
  addedText: {
    color: 'var(--bui-fg-success)',
  },
  removedText: {
    color: 'var(--bui-fg-danger)',
    textDecoration: 'line-through',
  },
  modifiedText: {
    color: 'var(--bui-fg-primary)',
  },
});

const BINARY_OPERATOR_DISPLAY_MAP: Record<string, string> = {
  '=': 'is',
  '!=': 'is not',
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
} as const;

const UNARY_OPERATOR_DISPLAY_MAP: Record<string, string> = {
  'is-null': 'is',
  'is-not-null': 'is not',
} as const;

const SET_OPERATOR_DISPLAY_MAP: Record<string, string> = {
  'any-of': 'is one of',
  'none-of': 'is not one of',
} as const;

const SEGMENT_OPERATOR_DISPLAY_MAP: Record<string, string> = {
  'in-segment': 'in segment',
  'not-in-segment': 'not in segment',
} as const;

const RANGE_OPERATOR_DISPLAY_MAP: Record<string, string> = {
  between: 'between',
  'not-between': 'not between',
} as const;

const ARRAY_OPERATOR_DISPLAY_MAP: Record<string, string> = {
  'match-all': 'all elements of',
  'match-any': 'any element of',
} as const;

const ROOT_DEPTH = 0;
const INITIAL_DEPTH_OFFSET = -1;

const DeltaValueRenderer = ({
  field,
  deltaValue,
}: {
  field: string;
  deltaValue: DeltaValue;
}) => {
  const classes = useStyles();
  const fieldOverride = useEvaluationContextField(field);
  const { status, before, after } = deltaValue;

  // Handle modified case specially - show both old and new values
  if (status === 'modified' && before !== undefined && after !== undefined) {
    const beforeValue = NormalizedExpression.valueToString(before);
    const afterValue = NormalizedExpression.valueToString(after);

    let beforeDisplay = beforeValue;
    let afterDisplay = afterValue;

    if (fieldOverride?.semanticType?.country?.format) {
      beforeDisplay = CountryUtils.getCountryLabel(beforeValue);
      afterDisplay = CountryUtils.getCountryLabel(afterValue);
    }

    return (
      <Tooltip title={`${beforeValue} → ${afterValue}`} arrow enterDelay={700}>
        <Expression.Value>
          <DeltaText
            oldText={beforeDisplay}
            newText={afterDisplay}
            disableDiff
          />
        </Expression.Value>
      </Tooltip>
    );
  }

  let value: string;
  let className: string | undefined;

  if (status === 'removed' && before !== undefined) {
    value = NormalizedExpression.valueToString(before);
    className = classes.removedText;
  } else if (status === 'added' && after !== undefined) {
    value = NormalizedExpression.valueToString(after);
    className = classes.addedText;
  } else {
    // unchanged or fallback
    if (after !== undefined) {
      value = NormalizedExpression.valueToString(after);
    } else if (before !== undefined) {
      value = NormalizedExpression.valueToString(before);
    } else {
      value = 'null';
    }
  }

  let node: React.ReactNode;

  if (fieldOverride?.semanticType?.entityReference) {
    node = <Expression.Value className={className}>{value}</Expression.Value>;
  } else if (fieldOverride?.semanticType?.country?.format) {
    node = (
      <Expression.Value className={className}>
        {CountryUtils.getCountryLabel(value)}
      </Expression.Value>
    );
  } else {
    node = <Expression.Value className={className}>{value}</Expression.Value>;
  }

  return (
    <Tooltip title={value} arrow enterDelay={700}>
      {node}
    </Tooltip>
  );
};

const DeltaOperatorRenderer = ({
  deltaOperator,
  displayMap,
}: {
  deltaOperator: DeltaOperator<string>;
  displayMap: Record<string, string>;
}) => {
  const classes = useStyles();
  const { status, before, after } = deltaOperator;

  let operator: string;
  let className: string | undefined;

  if (status === 'modified' && before !== undefined && after !== undefined) {
    const beforeOperator = displayMap[before] || before;
    const afterOperator = displayMap[after] || after;

    return (
      <Expression.Operator className={className}>
        <DeltaText oldText={beforeOperator} newText={afterOperator} />
      </Expression.Operator>
    );
  }

  if (status === 'removed' && before !== undefined) {
    operator = displayMap[before] || before;
    className = classes.removedText;
  } else if (status === 'added' && after !== undefined) {
    operator = displayMap[after] || after;
    className = classes.addedText;
  } else {
    // unchanged or fallback
    if (after !== undefined) {
      operator = displayMap[after] || after;
    } else if (before !== undefined) {
      operator = displayMap[before] || before;
    } else {
      operator = '';
    }
  }

  return (
    <Expression.Operator className={className}>{operator}</Expression.Operator>
  );
};

// Expression renderer components
const BinaryExpressionDeltaRenderer = ({
  expr,
}: {
  expr: NormalizedExpressionDelta.BinaryExpressionDelta;
}) => {
  return (
    <Expression.Root>
      <FieldName field={expr.attributeName} />
      <DeltaOperatorRenderer
        deltaOperator={expr.operator}
        displayMap={BINARY_OPERATOR_DISPLAY_MAP}
      />
      <DeltaValueRenderer field={expr.attributeName} deltaValue={expr.value} />
    </Expression.Root>
  );
};

const UnaryExpressionDeltaRenderer = ({
  expr,
}: {
  expr: NormalizedExpressionDelta.UnaryExpressionDelta;
}) => {
  return (
    <Expression.Root>
      <FieldName field={expr.attributeName} />
      <DeltaOperatorRenderer
        deltaOperator={expr.operator}
        displayMap={UNARY_OPERATOR_DISPLAY_MAP}
      />
      <Expression.Value>null</Expression.Value>
    </Expression.Root>
  );
};

const RangeExpressionDeltaRenderer = ({
  expr,
}: {
  expr: NormalizedExpressionDelta.RangeExpressionDelta;
}) => {
  const { range, operator } = expr;

  // Check if both are inclusive
  const startInclusive =
    range.startInclusive.after ?? range.startInclusive.before ?? true;
  const endInclusive =
    range.endInclusive.after ?? range.endInclusive.before ?? true;

  // Both inclusive - use "between"/"not between" format
  if (startInclusive && endInclusive) {
    return (
      <Expression.Root>
        <FieldName field={expr.attributeName} />
        <DeltaOperatorRenderer
          deltaOperator={operator}
          displayMap={RANGE_OPERATOR_DISPLAY_MAP}
        />
        <DeltaValueRenderer
          field={expr.attributeName}
          deltaValue={range.start}
        />
        <Expression.Operator>and</Expression.Operator>
        <DeltaValueRenderer field={expr.attributeName} deltaValue={range.end} />
      </Expression.Root>
    );
  }

  // Mixed inclusive/exclusive - use bracket notation
  const startBracket = startInclusive ? '[' : '(';
  const endBracket = endInclusive ? ']' : ')';

  return (
    <Expression.Root>
      <FieldName field={expr.attributeName} />
      <DeltaOperatorRenderer
        deltaOperator={operator}
        displayMap={RANGE_OPERATOR_DISPLAY_MAP}
      />
      <Expression.Operator>{startBracket}</Expression.Operator>
      <DeltaValueRenderer field={expr.attributeName} deltaValue={range.start} />
      <Expression.Operator>,</Expression.Operator>
      <DeltaValueRenderer field={expr.attributeName} deltaValue={range.end} />
      <Expression.Operator>{endBracket}</Expression.Operator>
    </Expression.Root>
  );
};

const SetExpressionDeltaRenderer = ({
  expr,
}: {
  expr: NormalizedExpressionDelta.SetExpressionDelta;
}) => {
  return (
    <Expression.Root>
      <FieldName field={expr.attributeName} />
      <DeltaOperatorRenderer
        deltaOperator={expr.operator}
        displayMap={SET_OPERATOR_DISPLAY_MAP}
      />

      {expr.values.map((deltaValue, i, arr) => {
        let valueKey: string;
        if (deltaValue.after !== undefined) {
          valueKey = NormalizedExpression.valueToString(deltaValue.after);
        } else if (deltaValue.before !== undefined) {
          valueKey = NormalizedExpression.valueToString(deltaValue.before);
        } else {
          valueKey = 'null';
        }
        return (
          <Fragment key={`${expr.attributeName}-${valueKey}-${i}`}>
            <DeltaValueRenderer
              field={expr.attributeName}
              deltaValue={deltaValue}
            />
            {i < arr.length - 1 && <Expression.Operator>•</Expression.Operator>}
          </Fragment>
        );
      })}
    </Expression.Root>
  );
};

const SegmentExpressionDeltaRenderer = ({
  expr,
}: {
  expr: NormalizedExpressionDelta.SegmentExpressionDelta;
}) => {
  const segmentIdValue = expr.segmentId.after || expr.segmentId.before || '';
  const segmentIdStr =
    typeof segmentIdValue === 'string'
      ? segmentIdValue
      : String(segmentIdValue);
  const segmentId = segmentIdStr.split('/').pop() || segmentIdStr;

  const { segment } = useSegment({
    name: segmentIdStr,
  });

  const classes = useStyles();
  const segmentIdStatus = expr.segmentId.status;

  let className: string | undefined;
  if (segmentIdStatus === 'added') {
    className = classes.addedText;
  } else if (segmentIdStatus === 'removed') {
    className = classes.removedText;
  } else if (segmentIdStatus === 'modified') {
    className = classes.modifiedText;
  }

  return (
    <Expression.Root>
      <DeltaOperatorRenderer
        deltaOperator={expr.operator}
        displayMap={SEGMENT_OPERATOR_DISPLAY_MAP}
      />
      <Expression.Value className={className}>
        {segment?.displayName ?? segmentId}
      </Expression.Value>
    </Expression.Root>
  );
};

const LogicalExpressionDeltaRenderer = ({
  expr,
  depth = 0,
}: {
  expr: NormalizedExpressionDelta.LogicalExpressionDelta;
  depth?: number;
}) => {
  const classes = useStyles();
  const expressions = expr.operands.map((operand, index) => {
    // Determine if the operator before this operand should be styled
    // The operator appears before the operand, so we check the operand's status
    const operandStatus = operand.status;
    const operatorText =
      depth >= ROOT_DEPTH
        ? expr.operator.after || expr.operator.before || 'and'
        : (expr.operator.after || expr.operator.before || 'and').toUpperCase();

    // Style the operator based on the operand's status
    let operatorClassName: string | undefined;
    if (operandStatus === 'added') {
      operatorClassName = classes.addedText;
    } else if (operandStatus === 'removed') {
      operatorClassName = classes.removedText;
    }

    return (
      <Flex
        key={`logical-${depth}-${index}`}
        direction="row"
        align="baseline"
        gap="2"
      >
        {depth >= INITIAL_DEPTH_OFFSET && index !== 0 && (
          <Text
            color="secondary"
            variant="body-medium"
            className={operatorClassName}
          >
            {operatorText}
          </Text>
        )}

        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        <NormalizedExpressionDeltaRenderer
          expression={operand}
          depth={depth + 1}
        />
      </Flex>
    );
  });

  return <>{expressions}</>;
};

const ArrayExpressionDeltaRenderer = ({
  expr,
}: {
  expr: NormalizedExpressionDelta.ArrayExpressionDelta;
}) => {
  const inner = useMemo(() => {
    // Use existing renderers but extract content without Expression.Root wrapper
    switch (expr.rule.type) {
      case 'binary':
        return (
          <BinaryExpressionDeltaRenderer
            expr={{
              ...expr.rule,
              attributeName: expr.attributeName,
              status: expr.status,
            }}
          />
        );
      case 'range':
        return (
          <RangeExpressionDeltaRenderer
            expr={{
              ...expr.rule,
              attributeName: expr.attributeName,
              status: expr.status,
            }}
          />
        );
      case 'set':
        return (
          <SetExpressionDeltaRenderer
            expr={{
              ...expr.rule,
              attributeName: expr.attributeName,
              status: expr.status,
            }}
          />
        );
      default:
        return null;
    }
  }, [expr.rule, expr.attributeName, expr.status]);

  return (
    <Expression.Root>
      <DeltaOperatorRenderer
        deltaOperator={expr.operator}
        displayMap={ARRAY_OPERATOR_DISPLAY_MAP}
      />
      {inner}
    </Expression.Root>
  );
};

export const NormalizedExpressionDeltaRenderer = ({
  expression,
  depth = INITIAL_DEPTH_OFFSET,
}: {
  expression: NormalizedExpressionDelta.ExpressionDelta;
  depth?: number;
}) => {
  const classes = useStyles();
  const content = useMemo(() => {
    switch (expression.type) {
      case 'binary':
        return <BinaryExpressionDeltaRenderer expr={expression} />;
      case 'unary':
        return <UnaryExpressionDeltaRenderer expr={expression} />;
      case 'range':
        return <RangeExpressionDeltaRenderer expr={expression} />;
      case 'set':
        return <SetExpressionDeltaRenderer expr={expression} />;
      case 'segment':
        return <SegmentExpressionDeltaRenderer expr={expression} />;
      case 'logical':
        return (
          <LogicalExpressionDeltaRenderer expr={expression} depth={depth} />
        );
      case 'array':
        return <ArrayExpressionDeltaRenderer expr={expression} />;
      default:
        return null;
    }
  }, [expression, depth]);

  if (
    depth === ROOT_DEPTH ||
    (depth === INITIAL_DEPTH_OFFSET && expression.type !== 'logical')
  ) {
    return (
      <ExpressionGroup
        className={classNames({
          [classes.addedBackground]: expression.status === 'added',
          [classes.removedBackground]: expression.status === 'removed',
        })}
      >
        {content}
      </ExpressionGroup>
    );
  }

  return content;
};
